import React, { useEffect } from 'react';
import { Text, MessageBox, Button } from '@ui5/webcomponents-react';
import { useError } from '../ErrorContext/ErrorContext';
import { useNavigate } from 'react-router-dom';
import { onError, offError } from '../../utils/errorEmitter';
import { useTranslations } from '@/hooks/useTranslations';
import * as storageUtils from '@/utils/storageUtils';

const CLOSE_BUTTON = 'CLOSE_BUTTON';
const LOGIN_BUTTON = 'LOGIN_BUTTON';
const HOME_BUTTON = 'HOME_BUTTON';

interface ButtonConfig {
    text: string;
    action: () => void;
}

type ButtonType = typeof CLOSE_BUTTON | typeof LOGIN_BUTTON | typeof HOME_BUTTON;

export const ErrorDisplay: React.FC = () => {
    const { error, showError, hideError } = useError();
    const navigate = useNavigate();
    const { t } = useTranslations();

    const redirectToLogin = () => {
        storageUtils.removeItem(['refreshToken', 'token', 'user', 'code', 'state']);
        navigate('/login');
        hideError();
    };

    const navigateToHome = () => {
        navigate('/');
        hideError();
    };

    // Button type to configuration mapping
    const BUTTON_CONFIGS: Record<ButtonType, ButtonConfig> = {
        [CLOSE_BUTTON]: {
            text: t('CLOSE_BUTTON'),
            action: hideError,
        },
        [LOGIN_BUTTON]: {
            text: t('LOGIN_BUTTON'),
            action: redirectToLogin,
        },
        [HOME_BUTTON]: {
            text: t('HOME_BUTTON'),
            action: navigateToHome,
        },
    };

    const getButtonType = (statusCode: string): ButtonType => {
        const code = Number(statusCode);

        switch (code) {
            case 400:
                return CLOSE_BUTTON;
            case 401:
                return LOGIN_BUTTON;
            case 404:
                return HOME_BUTTON;
            default:
                return CLOSE_BUTTON;
        }
    };

    const buttonType: ButtonType = error ? getButtonType(error.statusCode) : CLOSE_BUTTON;

    useEffect(() => {
        const handleError = ({ title, message, statusCode }: { title: string; message: string; statusCode: string }) => {
            // Check if status code is 429, if so redirect to the too-many-requests page
            if (statusCode === '429') {
                navigate('/error-too-many-requests', { state: { fromError: true } });
                return;
            }

            showError(title, message, statusCode);
        };

        onError(handleError);

        return () => {
            offError(handleError);
        };
    }, [showError, navigate]);

    // If error is 429 or no error, don't show the MessageBox
    if (!error || error.statusCode === '429') return null;

    return (
        <MessageBox
            type="Warning"
            data-testid="4xx_error_messageBox"
            titleText={t(error.title)}
            open={true}
            actions={[
                <Button
                    data-testid="4xx_error_messageBox_footer_button"
                    key={buttonType}
                    design="Emphasized"
                    onClick={BUTTON_CONFIGS[buttonType].action}
                >
                    {BUTTON_CONFIGS[buttonType].text}
                </Button>,
            ]}
        >
            <Text>{t(error.message)}</Text>
        </MessageBox>
    );
};
